import { SiteHeader } from "@/components/site-header"

export default function DbDrizzleGuidePage() {
  return (
    <>
      <SiteHeader title="Gestión de DB + Drizzle" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-3xl space-y-10">
          <p className="text-sm text-muted-foreground">
            Guía completa del flujo de migraciones con Drizzle: desarrollo, producción y resolución de problemas.
          </p>

          {/* ─── Flujo de trabajo ─── */}
          <section className="space-y-4">
            <h2 className="text-base font-semibold">Flujo de trabajo</h2>

            <div>
              <p className="text-sm font-medium mb-2">Desarrollo — reset completo</p>
              <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`bun run db:fresh       # reset + migrate + seed (DB limpia desde cero)`}
              </pre>
              <p className="text-xs text-muted-foreground mt-1">
                Dropea los schemas <span className="font-mono">public</span> + <span className="font-mono">drizzle</span>, aplica todas las migraciones en orden (incluyendo secuencias y objetos SQL manuales), y ejecuta los seeders.
              </p>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Desarrollo — iteración rápida de schema</p>
              <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`# 1. Editar schema.ts
# 2. Aplicar directo a la DB (sin migración):
bun run db:push`}
              </pre>
              <p className="text-xs text-muted-foreground mt-1">
                <span className="font-mono">db:push</span> solo sincroniza lo definido en <span className="font-mono">schema.ts</span>. No crea secuencias ni objetos SQL manuales. Nunca usar en prod.
              </p>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Preparar cambios para producción</p>
              <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`# 1. Editar schema.ts
# 2. Generar migración SQL con el diff:
bun run db:generate
# 3. Revisar el archivo .sql generado en src/db/migrations/
# 4. Commitear al repo`}
              </pre>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Producción — aplicar migraciones</p>
              <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`bun run db:migrate     # aplica migraciones pendientes`}
              </pre>
              <p className="text-xs text-muted-foreground mt-1">
                Nunca usar <span className="font-mono">db:push</span> ni <span className="font-mono">db:fresh</span> en producción.
              </p>
            </div>

            <div className="rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 px-4 py-3 text-sm text-blue-800 dark:text-blue-300">
              <strong>Regla clave:</strong> <span className="font-mono text-xs">db:fresh</span> usa <span className="font-mono text-xs">db:migrate</span> internamente, no <span className="font-mono text-xs">db:push</span>. Esto garantiza que secuencias, triggers y otros objetos SQL manuales siempre se crean correctamente.
            </div>
          </section>

          {/* ─── Migraciones SQL manuales ─── */}
          <section className="space-y-4">
            <h2 className="text-base font-semibold">Migraciones SQL manuales (secuencias, funciones, triggers)</h2>
            <p className="text-sm text-muted-foreground">
              Para objetos que <span className="font-mono text-xs">drizzle-kit generate</span> no genera automáticamente (secuencias, funciones PL/pgSQL, triggers), crear un archivo <span className="font-mono text-xs">.sql</span> manual en <span className="font-mono text-xs">src/db/migrations/</span>.
            </p>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">1. Crear el archivo con el nombre que corresponda:</p>
                <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`# Ejemplo: src/db/migrations/0001_connection_sequences.sql`}
                </pre>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">2. Escribir el SQL con separadores entre statements:</p>
                <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`CREATE SEQUENCE IF NOT EXISTS mi_secuencia;--> statement-breakpoint

SELECT setval('mi_secuencia', GREATEST(1, COALESCE(
  (SELECT MAX(...) FROM ...), 1
)));`}
                </pre>
                <p className="text-xs text-muted-foreground mt-1">
                  <strong>Importante:</strong> agregar <span className="font-mono text-xs">--&gt; statement-breakpoint</span> después de cada <span className="font-mono text-xs">;</span> para que Drizzle parsee los statements correctamente.
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">3. Registrar la entrada en <span className="font-mono">meta/_journal.json</span>:</p>
                <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`{
  "idx": 1,
  "version": "7",
  "when": 1781358300000,
  "tag": "0001_connection_sequences",
  "breakpoints": true
}`}
                </pre>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">4. Usar <span className="font-mono">GREATEST(1, ...)</span> en <span className="font-mono">setval</span> para evitar error "value 0 is out of bounds":</p>
                <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`-- ❌ falla si no hay datos (COALESCE devuelve 0)
SELECT setval('seq', COALESCE((...), 0));

-- ✅ GREATEST(1, ...) garantiza mínimo 1
SELECT setval('seq', GREATEST(1, COALESCE((...), 1)));`}
                </pre>
              </div>
            </div>
          </section>

          {/* ─── Problema: bootstrapping en prod existente ─── */}
          <section className="space-y-4">
            <h2 className="text-base font-semibold">
              Bootstrapping: <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">db:migrate</span> en una DB existente
            </h2>
            <p className="text-sm text-muted-foreground">
              Si la DB fue creada sin <span className="font-mono text-xs">db:migrate</span> (ej. con <span className="font-mono text-xs">schema.sql</span> directo o <span className="font-mono text-xs">db:push</span>), Drizzle no sabe qué migraciones ya están aplicadas e intenta re-ejecutar todo. El error típico es <span className="font-mono text-xs">type "fleet_type" already exists</span>.
            </p>
            <div>
              <p className="text-sm font-medium mb-2">Solución: registrar los hashes manualmente</p>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">1. Obtener el hash SHA-256 de cada migración ya aplicada:</p>
                  <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`shasum -a 256 apps/api/src/db/migrations/0000_initial_schema.sql | awk '{print $1}'`}
                  </pre>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">2. Crear el schema de Drizzle y registrar los hashes:</p>
                  <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`psql 'postgresql://...' -c "
CREATE SCHEMA IF NOT EXISTS drizzle;
CREATE TABLE IF NOT EXISTS drizzle.__drizzle_migrations (
  id SERIAL PRIMARY KEY,
  hash TEXT NOT NULL,
  created_at BIGINT
);
INSERT INTO drizzle.__drizzle_migrations (hash, created_at)
VALUES ('<hash_0000>', EXTRACT(EPOCH FROM NOW())::BIGINT * 1000);
-- Repetir para cada migración ya aplicada (0001, etc.)
"`}
                  </pre>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">3. Correr migrate — ahora solo aplica las pendientes:</p>
                  <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`bun run db:migrate`}
                  </pre>
                </div>
              </div>
            </div>
            <div className="rounded-md border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800 px-4 py-3 text-sm text-green-800 dark:text-green-300">
              <strong>Para nuevas instalaciones:</strong> arrancar siempre con <span className="font-mono text-xs">db:migrate</span> (o <span className="font-mono text-xs">db:fresh</span> en dev) para evitar este problema.
            </div>
          </section>

          {/* ─── Verificaciones útiles ─── */}
          <section className="space-y-4">
            <h2 className="text-base font-semibold">Verificaciones útiles</h2>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Ver migraciones aplicadas:</p>
                <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`psql 'postgresql://...' -c "
SELECT id, substring(hash,1,16) AS hash,
       to_timestamp(created_at/1000) AS applied_at
FROM drizzle.__drizzle_migrations ORDER BY id;"`}
                </pre>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Ver secuencias y sus valores:</p>
                <pre className="bg-muted rounded-md px-4 py-3 text-xs overflow-x-auto">
{`psql 'postgresql://...' -c "
SELECT sequencename, last_value FROM pg_sequences
WHERE sequencename IN (
  'smart_gt_crt_connection_seq',
  'smart_gt_detail_connection_seq'
);"`}
                </pre>
              </div>
            </div>
          </section>

          {/* ─── Referencia rápida ─── */}
          <section className="space-y-3">
            <h2 className="text-base font-semibold">Referencia rápida de comandos</h2>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Comando</th>
                    <th className="text-left px-4 py-2 font-medium">Qué hace</th>
                    <th className="text-left px-4 py-2 font-medium">Uso</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-xs">
                  <tr>
                    <td className="px-4 py-2 font-mono">db:fresh</td>
                    <td className="px-4 py-2 text-muted-foreground">reset + migrate + seed</td>
                    <td className="px-4 py-2 text-muted-foreground">Dev — DB limpia desde cero</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">db:push</td>
                    <td className="px-4 py-2 text-muted-foreground">Sync schema.ts → DB directo</td>
                    <td className="px-4 py-2 text-muted-foreground">Dev — iteración rápida (no crea secuencias)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">db:generate</td>
                    <td className="px-4 py-2 text-muted-foreground">Genera .sql con el diff de schema.ts</td>
                    <td className="px-4 py-2 text-muted-foreground">Preparar migración para prod</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">db:migrate</td>
                    <td className="px-4 py-2 text-muted-foreground">Aplica migraciones pendientes</td>
                    <td className="px-4 py-2 text-muted-foreground">Dev + Prod</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono">db:studio</td>
                    <td className="px-4 py-2 text-muted-foreground">Abre Drizzle Studio</td>
                    <td className="px-4 py-2 text-muted-foreground">Dev — explorar datos</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
              <strong>Nota:</strong> todos los comandos se pueden correr desde la raíz del monorepo (<span className="font-mono text-xs">bun run db:*</span>). También desde <span className="font-mono text-xs">apps/api</span> directamente.
            </div>
          </section>

        </div>
      </div>
    </>
  )
}
